import type { PlanTier } from "@apsu/shared";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

type BillingData = {
	planTier: string;
	planName: string;
	planFeatures: PlanTier["features"] | null;
	subscriptionStatus: string;
	paymentFailure: number;
	hasStripeCustomer: boolean;
	availablePlans: PlanTier[];
};

type Invoice = {
	id: string;
	date: number;
	amount: number;
	currency: string;
	status: string | null;
	hostedInvoiceUrl: string | null;
};

export function BillingPage() {
	const { workspaceSlug } = useParams<{ workspaceSlug: string }>();
	const [data, setData] = useState<BillingData | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [actionLoading, setActionLoading] = useState(false);
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [invoicesLoading, setInvoicesLoading] = useState(false);
	const [invoicesHasMore, setInvoicesHasMore] = useState(false);
	const [invoicesCursor, setInvoicesCursor] = useState<string | null>(null);

	const fetchInvoices = useCallback(
		async (cursor?: string | null) => {
			setInvoicesLoading(true);
			try {
				const params = new URLSearchParams({ limit: "10" });
				if (cursor) params.set("starting_after", cursor);
				const res = await fetch(
					`/api/tenant/${workspaceSlug}/billing/invoices?${params}`,
					{
						credentials: "include",
						headers: { Origin: window.location.origin },
					},
				);
				if (!res.ok) return;
				const json = (await res.json()) as {
					invoices: Invoice[];
					hasMore: boolean;
					nextCursor: string | null;
				};
				setInvoices((prev) =>
					cursor ? [...prev, ...json.invoices] : json.invoices,
				);
				setInvoicesHasMore(json.hasMore);
				setInvoicesCursor(json.nextCursor);
			} finally {
				setInvoicesLoading(false);
			}
		},
		[workspaceSlug],
	);

	useEffect(() => {
		async function fetchBilling() {
			try {
				const res = await fetch(`/api/tenant/${workspaceSlug}/billing`, {
					credentials: "include",
					headers: { Origin: window.location.origin },
				});
				if (!res.ok) {
					setError(`Failed to load billing info (${res.status})`);
					return;
				}
				const json = (await res.json()) as BillingData;
				setData(json);
			} catch {
				setError("Network error");
			} finally {
				setLoading(false);
			}
		}
		fetchBilling();
	}, [workspaceSlug]);

	useEffect(() => {
		if (data?.hasStripeCustomer) {
			fetchInvoices();
		}
	}, [data?.hasStripeCustomer, fetchInvoices]);

	async function handleCheckout(planTier: string) {
		setActionLoading(true);
		try {
			const currentUrl = window.location.href;
			const res = await fetch(`/api/tenant/${workspaceSlug}/billing/checkout`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({
					planTier,
					successUrl: currentUrl,
					cancelUrl: currentUrl,
				}),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { error?: string } | null)?.error ??
						`Checkout failed (${res.status})`,
				);
				return;
			}
			const { url } = (await res.json()) as { url: string };
			window.location.href = url;
		} catch {
			setError("Network error");
		} finally {
			setActionLoading(false);
		}
	}

	async function handlePortal() {
		setActionLoading(true);
		try {
			const res = await fetch(`/api/tenant/${workspaceSlug}/billing/portal`, {
				method: "POST",
				credentials: "include",
				headers: {
					"Content-Type": "application/json",
					Origin: window.location.origin,
				},
				body: JSON.stringify({ returnUrl: window.location.href }),
			});
			if (!res.ok) {
				const body = await res.json().catch(() => null);
				setError(
					(body as { error?: string } | null)?.error ??
						`Portal redirect failed (${res.status})`,
				);
				return;
			}
			const { url } = (await res.json()) as { url: string };
			window.location.href = url;
		} catch {
			setError("Network error");
		} finally {
			setActionLoading(false);
		}
	}

	if (loading) {
		return (
			<div className="max-w-2xl mx-auto mt-8">
				<p className="text-gray-500">Loading billing info…</p>
			</div>
		);
	}

	if (error && !data) {
		return (
			<div className="max-w-2xl mx-auto mt-8">
				<p className="text-red-600">{error}</p>
			</div>
		);
	}

	if (!data) return null;

	const isActive = data.subscriptionStatus === "active";
	const upgradePlans = data.availablePlans.filter(
		(p) => p.id !== "free" && p.id !== data.planTier,
	);

	return (
		<div className="max-w-2xl mx-auto mt-8">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">Billing</h1>

			{error && <p className="text-red-600 text-sm mb-4">{error}</p>}

			{data.paymentFailure > 0 && (
				<div className="bg-red-50 border border-red-200 rounded p-4 mb-6">
					<p className="text-red-800 text-sm font-medium">
						Payment failed. Please update your payment method to avoid service
						interruption.
					</p>
				</div>
			)}

			<div className="bg-white border border-gray-200 rounded p-6 mb-6">
				<h2 className="text-lg font-semibold text-gray-900 mb-2">
					Current Plan
				</h2>
				<p className="text-gray-700 text-lg font-medium">{data.planName}</p>
				<p className="text-gray-500 text-sm mt-1">
					Status: <span className="capitalize">{data.subscriptionStatus}</span>
				</p>
				{data.planFeatures && (
					<ul className="mt-3 space-y-1 text-sm text-gray-600">
						<li>
							Max orgs:{" "}
							{data.planFeatures.maxOrgs === -1
								? "Unlimited"
								: data.planFeatures.maxOrgs}
						</li>
						<li>Audit log: {data.planFeatures.auditLog ? "Yes" : "No"}</li>
						<li>
							Priority support:{" "}
							{data.planFeatures.prioritySupport ? "Yes" : "No"}
						</li>
					</ul>
				)}
			</div>

			{isActive && (
				<button
					type="button"
					onClick={handlePortal}
					disabled={actionLoading}
					className="rounded bg-gray-100 border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50 mb-6"
				>
					Manage Subscription
				</button>
			)}

			{upgradePlans.length > 0 && (
				<div>
					<h2 className="text-lg font-semibold text-gray-900 mb-4">
						{isActive ? "Change Plan" : "Upgrade"}
					</h2>
					<div className="grid gap-4 sm:grid-cols-2">
						{upgradePlans.map((plan) => (
							<div
								key={plan.id}
								className="bg-white border border-gray-200 rounded p-4 flex flex-col"
							>
								<h3 className="font-semibold text-gray-900">{plan.name}</h3>
								<ul className="mt-2 space-y-1 text-sm text-gray-600 flex-1">
									<li>
										Max orgs:{" "}
										{plan.features.maxOrgs === -1
											? "Unlimited"
											: plan.features.maxOrgs}
									</li>
									<li>Audit log: {plan.features.auditLog ? "Yes" : "No"}</li>
									<li>
										Priority support:{" "}
										{plan.features.prioritySupport ? "Yes" : "No"}
									</li>
								</ul>
								<button
									type="button"
									onClick={() => handleCheckout(plan.id)}
									disabled={actionLoading}
									className="mt-4 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
								>
									{isActive
										? `Switch to ${plan.name}`
										: `Upgrade to ${plan.name}`}
								</button>
							</div>
						))}
					</div>
				</div>
			)}

			{data.hasStripeCustomer && (
				<div className="mt-8">
					<h2 className="text-lg font-semibold text-gray-900 mb-4">
						Invoice History
					</h2>
					{invoices.length === 0 && !invoicesLoading ? (
						<p className="text-gray-500 text-sm">No invoices yet.</p>
					) : (
						<div className="bg-white border border-gray-200 rounded overflow-hidden">
							<table className="w-full text-sm">
								<thead className="bg-gray-50 border-b border-gray-200">
									<tr>
										<th className="text-left px-4 py-2 font-medium text-gray-600">
											Date
										</th>
										<th className="text-left px-4 py-2 font-medium text-gray-600">
											Amount
										</th>
										<th className="text-left px-4 py-2 font-medium text-gray-600">
											Status
										</th>
										<th className="text-right px-4 py-2 font-medium text-gray-600" />
									</tr>
								</thead>
								<tbody>
									{invoices.map((inv) => (
										<tr
											key={inv.id}
											className="border-b border-gray-100 last:border-0"
										>
											<td className="px-4 py-3 text-gray-700">
												{new Date(inv.date * 1000).toLocaleDateString()}
											</td>
											<td className="px-4 py-3 text-gray-700">
												{(inv.amount / 100).toLocaleString(undefined, {
													style: "currency",
													currency: inv.currency,
												})}
											</td>
											<td className="px-4 py-3">
												<InvoiceStatusBadge status={inv.status} />
											</td>
											<td className="px-4 py-3 text-right">
												{inv.hostedInvoiceUrl && (
													<a
														href={inv.hostedInvoiceUrl}
														target="_blank"
														rel="noopener noreferrer"
														className="text-blue-600 hover:text-blue-800 text-sm font-medium"
													>
														View
													</a>
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
					{invoicesHasMore && (
						<button
							type="button"
							onClick={() => fetchInvoices(invoicesCursor)}
							disabled={invoicesLoading}
							className="mt-3 rounded bg-gray-100 border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
						>
							{invoicesLoading ? "Loading…" : "Load More"}
						</button>
					)}
				</div>
			)}
		</div>
	);
}

function InvoiceStatusBadge({ status }: { status: string | null }) {
	const styles: Record<string, string> = {
		paid: "bg-green-100 text-green-800",
		open: "bg-yellow-100 text-yellow-800",
	};
	const cls = styles[status ?? ""] ?? "bg-gray-100 text-gray-600";
	return (
		<span
			className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
		>
			{status ?? "unknown"}
		</span>
	);
}
